import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, XCircle, Loader2, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VerifyEmail() {
  const { token } = useParams();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState('');

  useEffect(() => {
    verifyEmail();
  }, [token]);

  const verifyEmail = async () => {
    try {
      const res = await axios.get(`${API}/businesses/verify/${token}`);
      setStatus('success');
      setMessage(res.data.message);
    } catch (e) {
      setStatus('error');
      setMessage(e.response?.data?.detail || "Erreur de vérification");
    }
  };

  return (
    <div className="min-h-screen bg-sand flex items-center justify-center px-6">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-lagoon-100">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <Link to="/" className="flex items-center gap-2" data-testid="home-logo">
            <div className="w-10 h-10 rounded-full bg-lagoon flex items-center justify-center">
              <MapPin className="w-5 h-5 text-white" />
            </div>
            <span className="font-serif text-xl font-semibold text-lagoon-900">Annuaire Polynésien</span>
          </Link>
        </div>
      </header>

      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="glass-card rounded-3xl p-8 md:p-12 max-w-lg w-full text-center"
      >
        {status === 'loading' && (
          <>
            <div className="w-20 h-20 rounded-full bg-lagoon-50 flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-10 h-10 text-lagoon animate-spin" />
            </div>
            <h1 className="font-serif text-2xl font-bold text-lagoon-900 mb-4">
              Vérification en cours...
            </h1>
            <p className="text-gray-600">
              Veuillez patienter pendant que nous vérifions votre compte.
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
            <h1 className="font-serif text-2xl md:text-3xl font-bold text-lagoon-900 mb-4" data-testid="success-title">
              Compte vérifié !
            </h1>
            <p className="text-gray-600 mb-8" data-testid="success-message">
              {message}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/recherche">
                <Button className="btn-coral" data-testid="browse-btn">
                  Parcourir l'annuaire
                </Button>
              </Link>
              <Link to="/">
                <Button variant="outline" className="border-lagoon text-lagoon hover:bg-lagoon-50" data-testid="home-btn">
                  Retour à l'accueil
                </Button>
              </Link>
            </div>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-20 h-20 rounded-full bg-coral/10 flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-coral" />
            </div>
            <h1 className="font-serif text-2xl md:text-3xl font-bold text-lagoon-900 mb-4" data-testid="error-title">
              Erreur de vérification
            </h1>
            <p className="text-gray-600 mb-8" data-testid="error-message">
              {message}
            </p>
            <Link to="/">
              <Button className="btn-primary" data-testid="home-btn">
                Retour à l'accueil
              </Button>
            </Link>
          </>
        )}
      </motion.div>
    </div>
  );
}
